/**
 * Multi-account initialization
 *
 * This module handles the initialization of multi-account support on page load.
 * When the app loads, it checks if:
 * 1. Multi-account feature is enabled
 * 2. User is logged in
 * 3. A return URL exists in sessionStorage (indicating we just completed a multi-account sign-in)
 *
 * If all conditions are met, it fetches account metadata and adds the account to localStorage.
 */

import axios from "axios";
import Honeybadger from "@honeybadger-io/js";
import {
  isMultiAccountEnabled,
  initializeFromSession,
  pruneStaleAccounts,
} from "./account_storage";

const RETURN_URL_KEY = "tkc_multi_account_return_url";

/**
 * Initialize multi-account support
 * Called on app load to handle post-sign-in account setup
 */
export async function initializeMultiAccount() {
  // Skip if feature is disabled
  if (!isMultiAccountEnabled()) {
    return;
  }

  // Skip if not logged in
  if (!window._tkcCurrentUserId) {
    return;
  }

  // Check if we have a return URL (indicates we just completed multi-account sign-in)
  const returnUrl = sessionStorage.getItem(RETURN_URL_KEY);

  // Always ensure current session is in localStorage
  try {
    const response = await axios.get("/api/v1/account_switcher/metadata");
    const data = response.data;

    initializeFromSession({
      userId: data.user_id,
      email: data.email,
      fullName: data.full_name,
      avatarUrl: data.avatar_url,
      companyId: data.company_id,
      companyName: data.company_name,
      companyLogoUrl: data.company_logo_url,
      // Token fields - we rely on session cookies for auth
      accessToken: null,
      refreshToken: null,
      expiresAt: null,
    });

    // Prune stale accounts from localStorage that no longer have valid
    // server sessions (e.g., after browser/system restart clears sessions).
    if (data.authenticated_user_ids) {
      pruneStaleAccounts(data.authenticated_user_ids);
    }

    // Notify hooks that localStorage is now up-to-date so they can re-sync
    window.dispatchEvent(new Event("tkc:accounts-updated"));

    // After adding a new account, always redirect to home page for consistent UX
    if (returnUrl) {
      sessionStorage.removeItem(RETURN_URL_KEY);
      // Always redirect to home page after adding account
      if (window.location.pathname !== "/") {
        window.location.href = "/";
        return;
      }
    }
  } catch (error) {
    Honeybadger.notify(error);
  }
}
